import React, { useState, useMemo } from 'react';
import { Card } from 'pokemon-tcg-sdk-typescript';
import { useCollectionStore, CardFinish } from '../lib/collectionStore';
import { useProfileStore } from '../lib/supabase';
import { Plus, Minus, Loader2, PackagePlus, PackageMinus } from 'lucide-react';
import { useBatchCardFinishes } from '../hooks/useBatchCardFinishes';
import { ALL_FINISH_OPTIONS, getFinishOptions } from './CollectionButton';

interface AddCompleteSetButtonProps {
  cards: Card[];
  setName: string;
  className?: string;
}

export default function AddCompleteSetButton({ cards, setName, className = '' }: AddCompleteSetButtonProps) {
  const { isAuthenticated } = useProfileStore();
  const { addCompleteSet, removeCompleteSet, collection } = useCollectionStore();
  const [isAdding, setIsAdding] = useState(false);
  const [isRemoving, setIsRemoving] = useState(false);
  const [showAddOptions, setShowAddOptions] = useState(false);
  const [showRemoveOptions, setShowRemoveOptions] = useState(false);

  const cardIds = useMemo(() => cards.map(c => c.id), [cards]);
  const finishMap = useBatchCardFinishes(cardIds);

  const finishOptions = useMemo(() => {
    if (finishMap.size === 0) return ALL_FINISH_OPTIONS;
    const setFinishes = new Set<CardFinish>();
    for (const finishes of finishMap.values()) {
      for (const f of finishes) setFinishes.add(f);
    }
    if (setFinishes.size === 0) return ALL_FINISH_OPTIONS;
    return getFinishOptions([...setFinishes]);
  }, [finishMap]);

  const handleAddCompleteSet = async (finish: CardFinish) => {
    if (!isAuthenticated) {
      window.location.href = '/auth?register=true';
      return;
    }

    if (!cards || cards.length === 0) {
      console.error('No cards available to add');
      return;
    }

    setIsAdding(true);

    try {
      await addCompleteSet(cards, finish);

      // Dispatch success event
      const event = new CustomEvent('completeSetAdded', { 
        detail: { setName, finish, cardCount: cards.length }
      });
      document.dispatchEvent(event);

    } catch (error) {
      console.error(`Error adding complete set:`, error);
    } finally {
      setIsAdding(false);
      setShowAddOptions(false);
    }
  };

  const handleRemoveCompleteSet = async (finish?: CardFinish) => {
    if (!isAuthenticated) {
      window.location.href = '/auth?register=true';
      return;
    }

    if (!cards || cards.length === 0) {
      console.error('No cards available to remove');
      return;
    }

    const setId = cards[0]?.set?.id;
    if (!setId) {
      console.error('No set ID available');
      return;
    }

    setIsRemoving(true);

    try {
      await removeCompleteSet(setId, finish);

      // Dispatch success event
      const event = new CustomEvent('completeSetRemoved', { 
        detail: { setName, finish: finish || 'all finishes', setId }
      });
      document.dispatchEvent(event);

    } catch (error) {
      console.error(`Error removing complete set:`, error);
    } finally {
      setIsRemoving(false);
      setShowRemoveOptions(false);
    }
  };

  // Check if user has any cards from this set
  const setId = cards[0]?.set?.id;
  const hasCardsFromSet = setId ? collection.some(card => card.setId === setId) : false;

  if (!isAuthenticated) {
    return (
      <button
        onClick={() => window.location.href = '/auth?register=true'}
        className={`flex items-center gap-2 px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white rounded-lg transition-colors ${className}`}
      >
        <PackagePlus size={16} />
        <span>Sign in to Add Complete Set</span>
      </button>
    );
  }

  return (
    <div className={`relative ${className}`}>
      <div className="flex gap-2">
        <button
          onClick={() => setShowAddOptions(!showAddOptions)}
          disabled={isAdding || isRemoving || cards.length === 0}
          className="flex items-center gap-2 px-4 py-2 bg-green-600 hover:bg-green-700 disabled:opacity-50 disabled:cursor-not-allowed text-white rounded-lg transition-colors"
        >
          {isAdding ? (
            <>
              <Loader2 size={16} className="animate-spin" />
              <span>Adding Set...</span>
            </>
          ) : (
            <>
              <PackagePlus size={16} />
              <span>Add Complete Set ({cards.length} cards)</span>
            </>
          )}
        </button>

        {hasCardsFromSet && (
          <button
            onClick={() => setShowRemoveOptions(!showRemoveOptions)}
            disabled={isAdding || isRemoving || cards.length === 0}
            className="flex items-center gap-2 px-4 py-2 bg-red-600 hover:bg-red-700 disabled:opacity-50 disabled:cursor-not-allowed text-white rounded-lg transition-colors"
          >
            {isRemoving ? (
              <>
                <Loader2 size={16} className="animate-spin" />
                <span>Removing Set...</span>
              </>
            ) : (
              <>
                <PackageMinus size={16} />
                <span>Remove Complete Set</span>
              </>
            )}
          </button>
        )}
      </div>

      {showAddOptions && !isAdding && (
        <div className="absolute top-full mt-2 left-0 bg-black/95 backdrop-blur-sm border border-white/20 rounded-lg p-4 min-w-80 z-50">
          <h3 className="text-sm font-medium mb-3">Select card finish to add:</h3>
          <div className="space-y-2">
            {finishOptions.map((option) => (
              <button
                key={option.value}
                onClick={() => handleAddCompleteSet(option.value)}
                className={`w-full flex items-center justify-between p-3 border ${option.color} bg-white/5 rounded-lg hover:bg-white/10 transition-colors text-left`}
              >
                <div>
                  <div className={`font-medium ${option.textColor}`}>{option.label}</div>
                  <div className="text-sm text-white/60">
                    Add all {cards.length} cards as {option.label.toLowerCase()}
                  </div>
                </div>
                <Plus size={16} className="text-white/60" />
              </button>
            ))}
          </div>
          <button
            onClick={() => setShowAddOptions(false)}
            className="w-full mt-3 px-3 py-2 text-sm text-white/60 hover:text-white border border-white/20 rounded-lg hover:bg-white/5 transition-colors"
          >
            Cancel
          </button>
        </div>
      )}

      {showRemoveOptions && !isRemoving && (
        <div className="absolute top-full mt-2 right-0 bg-black/95 backdrop-blur-sm border border-white/20 rounded-lg p-4 min-w-80 z-50">
          <h3 className="text-sm font-medium mb-3">Select cards to remove:</h3>
          <div className="space-y-2">
            <button
              onClick={() => handleRemoveCompleteSet()}
              className="w-full flex items-center justify-between p-3 border border-red-500/30 bg-red-500/10 rounded-lg hover:bg-red-500/20 transition-colors text-left"
            >
              <div>
                <div className="font-medium">All Cards</div>
                <div className="text-sm text-white/60">
                  Remove all cards from this set (all finishes)
                </div>
              </div>
              <Minus size={16} className="text-white/60" />
            </button>
            
            {finishOptions.map((option) => (
              <button
                key={option.value}
                onClick={() => handleRemoveCompleteSet(option.value)}
                className={`w-full flex items-center justify-between p-3 border ${option.color} bg-white/5 rounded-lg hover:bg-white/10 transition-colors text-left`}
              >
                <div>
                  <div className={`font-medium ${option.textColor}`}>{option.label} Only</div>
                  <div className="text-sm text-white/60">
                    Remove only {option.label.toLowerCase()} cards from this set
                  </div>
                </div>
                <Minus size={16} className="text-white/60" />
              </button>
            ))}
          </div>
          <button
            onClick={() => setShowRemoveOptions(false)}
            className="w-full mt-3 px-3 py-2 text-sm text-white/60 hover:text-white border border-white/20 rounded-lg hover:bg-white/5 transition-colors"
          >
            Cancel
          </button>
        </div>
      )}

      {isAdding && (
        <div className="absolute top-full mt-2 left-0 bg-black/95 backdrop-blur-sm border border-white/20 rounded-lg p-4 min-w-80 z-50">
          <div className="flex items-center gap-3">
            <Loader2 size={16} className="animate-spin text-green-500" />
            <span className="text-sm font-medium">Adding Complete Set ({cards.length} cards)...</span>
          </div>
          <p className="text-xs text-white/60 mt-2">
            Please wait while we add all cards to your collection.
          </p>
        </div>
      )}

      {isRemoving && (
        <div className="absolute top-full mt-2 right-0 bg-black/95 backdrop-blur-sm border border-white/20 rounded-lg p-4 min-w-80 z-50">
          <div className="flex items-center gap-3">
            <Loader2 size={16} className="animate-spin text-red-500" />
            <span className="text-sm font-medium">Removing Complete Set...</span>
          </div>
          <p className="text-xs text-white/60 mt-2">
            Please wait while we remove cards from your collection.
          </p>
        </div>
      )}
    </div>
  );
} 